// src/i18n.js
import {
  type Accessor,
  createContext,
  createSignal,
  type ParentComponent,
  useContext,
} from "solid-js";
import type { Get } from "type-fest";

// Import translations
import translationIT from "@/locales/it/translations.json";

// import translationEN from './locales/en/translations.json';
// import translationFR from './locales/fr/translations.json';
// import translationDE from './locales/de/translations.json';

// Infer translation type from the imported JSON
type Translation = typeof translationIT;

// All translations
const translations: Record<string, Translation> = {
  it: translationIT,
  // en: translationEN,
  // fr: translationFR,
  // de: translationDE
};

// Default language
const DEFAULT_LANGUAGE = "it";

// Helper to detect user's preferred language
function detectUserLanguage(): string {
  const storedLang = localStorage.getItem("userLanguage");
  if (storedLang && Object.keys(translations).includes(storedLang)) {
    return storedLang;
  }

  // Browser language detection
  const browserLang = navigator.language.split("-")[0];
  if (Object.keys(translations).includes(browserLang)) {
    return browserLang;
  }

  return DEFAULT_LANGUAGE;
}

// i18n context type
type I18nContextType = {
  lang: Accessor<string>;
  t: <T extends string>(
    key: T,
    params?: Record<string, string | number>,
  ) => Get<Translation, T> extends string ? string : Get<Translation, T>;
  changeLang: (newLang: string) => void;
  availableLanguages: string[];
};

// Create the i18n context
export const I18nContext = createContext<I18nContextType>();

export const I18nProvider: ParentComponent = (props) => {
  const [lang, setLang] = createSignal<string>(detectUserLanguage());

  // Change language and persist to localStorage
  const changeLang = (newLang: string) => {
    if (Object.keys(translations).includes(newLang)) {
      setLang(newLang);
      localStorage.setItem("userLanguage", newLang);
      // Optionally set HTML lang attribute
      document.documentElement.lang = newLang;
    }
  };

  // Translation function
  const t = <T extends string>(
    key: T,
    params: Record<string, string | number> = {},
  ): Get<Translation, T> extends string ? string : Get<Translation, T> => {
    const keys = key.split(".");
    let value: unknown = translations[lang()];

    // Navigate through nested keys
    for (const k of keys) {
      if (value && typeof value === "object" && k in value) {
        value = (value as Record<string, unknown>)[k];
      } else {
        // Key not found, fall back to default language
        if (lang() !== DEFAULT_LANGUAGE) {
          let defaultValue: unknown = translations[DEFAULT_LANGUAGE];
          for (const dk of keys) {
            if (
              defaultValue &&
              typeof defaultValue === "object" &&
              dk in defaultValue
            ) {
              defaultValue = (defaultValue as Record<string, unknown>)[dk];
            } else {
              return key as Get<Translation, T> extends string
                ? string
                : Get<Translation, T>;
            }
          }
          value = defaultValue;
        } else {
          return key as Get<Translation, T> extends string
            ? string
            : Get<Translation, T>;
        }
      }
    }

    // Handle string interpolation with {{param}}
    if (typeof value === "string") {
      return Object.entries(params).reduce((str, [paramKey, paramValue]) => {
        return str.replace(
          new RegExp(`{{${paramKey}}}`, "g"),
          String(paramValue),
        );
      }, value) as Get<Translation, T> extends string
        ? string
        : Get<Translation, T>;
    }

    return value as Get<Translation, T> extends string
      ? string
      : Get<Translation, T>;
  };

  const i18n: I18nContextType = {
    lang,
    t,
    changeLang,
    availableLanguages: Object.keys(translations),
  };

  return (
    <I18nContext.Provider value={i18n}>{props.children}</I18nContext.Provider>
  );
};

// Hook to use translations
export function useI18n(): I18nContextType {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error("useI18n must be used within an I18nProvider");
  }
  return context;
}
